import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MdArrowBack, MdMoreVert, MdHistory, MdExpandMore, MdExpandLess, MdOpenInNew } from "react-icons/md";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// Removed duplicate import of faBookmark
import BookmarkModal from "../modals/bookmark-modal";
import EmailModal from "../modals/email-modal";
import WhatsAppModal from "../modals/whatsapp-modal";
import BookmarkAlert from "../modals/succes_alert";
import "./ProjectPublish.css";
import Building3DView from "./Building3DView";
import ArchitectProfileDialog from "./dialogs/ArchitectProfileDialog";
import { reverseGeocode } from "../../utils/geocoding";
import { faBookmark } from '@fortawesome/free-regular-svg-icons';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../../firebase_config';

const logo = "/favicon.svg";

const API_BASE_URL = `${process.env.REACT_APP_API_BASE}/`;
const MEDIA_PREFIX = process.env.REACT_APP_API_BASE.replace("/v1", "");
const PROJECT_PARTNER_ENDPOINT = "api/project_partner/";

const ProjectPublish = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [projectPartners, setProjectPartners] = useState([]);
  const [hasFetchedProjectPartners, setHasFetchedProjectPartners] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isArchitectsExpanded, setIsArchitectsExpanded] = useState(false);
  const [bookmarkAlert, setBookmarkAlert] = useState(null);
  const [architectProfiles, setArchitectProfiles] = useState({});

  const [isBuilding3DOpen, setIsBuilding3DOpen] = useState(false);
  const [architectProfileOpen, setArchitectProfileOpen] = useState(false);
  const [selectedArchitect, setSelectedArchitect] = useState(null);

  // Bookmark modal state
  const [isBookmarkOpen, setIsBookmarkOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("");
  // Handle bookmark button click
  const handleBookmark = () => {
    setIsBookmarkOpen(true);
  };

  // Modal state for email/whatsapp
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isWhatsAppModalOpen, setIsWhatsAppModalOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  // Handle modal close
  const handleBookmarkClose = () => {
    setIsBookmarkOpen(false);
    setSelectedOption("");
  };

  // Handle modal selection
  const handleBookmarkSelect = (option) => {
    setIsBookmarkOpen(false);
    setSelectedOption("");
    if (option === "email") {
      setIsEmailModalOpen(true);
    } else if (option === "whatsapp") {
      // Check if WhatsApp number is stored and remembered
      const savedNumber = localStorage.getItem('whatsappNumber');
      const savedRemember = localStorage.getItem('whatsappRememberMe');
      if (savedNumber && savedRemember === 'true') {
        setWhatsappNumber(savedNumber);
        setRememberMe(true);
        // Directly call the WhatsApp handler
        handleWhatsAppContinue();
      } else {
        setIsWhatsAppModalOpen(true);
      }
    }
  };

  const handleEmailModalClose = () => {
    setIsEmailModalOpen(false);
    setEmail("");
    setRememberMe(false);
  };

  const handleWhatsAppModalClose = () => {
    setIsWhatsAppModalOpen(false);
    setWhatsappNumber("");
    setRememberMe(false);
  };

  // Helper to call the bookmark API
  const callBookmarkApi = async (method, value) => {
    if (!project) return;
    const BOOKMARK_API = 'api/milestone-updates/';
    const BASE_URL = API_BASE_URL;
    let body;
    if (method === 'whatsapp') {
      body = {
        project_id: project.id,
        phone_number: value,
        active: true,
      };
    } else {
      body = {
        method,
        value,
      };
    }
    try {
      const url = new URL(BOOKMARK_API, BASE_URL);
      const response = await fetch(url.toString(), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error('Bookmarking Failed');
      // Optionally show a success message or alert
    } catch (err) {
      alert('Error bookmarking project. Please try again.');
    }
  };

  const handleEmailContinue = async () => {
    setIsEmailModalOpen(false);
    await callBookmarkApi('email', email);
    setBookmarkAlert({ method: 'email', value: email });
    setEmail("");
    setRememberMe(false);
  };

  const handleWhatsAppContinue = async () => {
    setIsWhatsAppModalOpen(false);
    await callBookmarkApi('whatsapp', whatsappNumber);
    setBookmarkAlert({ method: 'whatsapp', value: whatsappNumber });
    setWhatsappNumber("");
    setRememberMe(false);
  };

  const handleBack = () => {
    navigate("/search");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }); // e.g., "Aug 30, 2024"
    } catch {
      return dateString;
    }
  };

  const resolveMediaUrl = (value) => {
    if (!value || typeof value !== "string") return null;
    return value.startsWith("http") ? value : `${MEDIA_PREFIX}${value}`;
  };

  const getArchitectInitials = (name) => {
    if (!name || typeof name !== "string") return "AR";
    const parts = name.trim().split(" ").filter(Boolean);
    if (parts.length === 0) return "AR";
    return parts.slice(0, 2).map((part) => part[0]?.toUpperCase() || "").join("");
  };

  const normalizePartnerItem = (partner, index) => ({
    name:
      partner?.name ||
      partner?.company_name ||
      partner?.title ||
      partner?.partner_name ||
      `Architect ${index + 1}`,
    logo: resolveMediaUrl(
      partner?.logo ||
      partner?.avatar ||
      partner?.image ||
      partner?.profile_picture,
    ) || "",
    regNo:
      partner?.registration_number ||
      partner?.reg_no ||
      partner?.registration_no ||
      partner?.license_no ||
      partner?.type ||
      "N/A",
    url: partner?.url || partner?.website || partner?.link || "",
  });

  const architectsSource = hasFetchedProjectPartners ? projectPartners : [];

  const architects = architectsSource.map((architect, index) => {
    if (typeof architect === "string") {
      return {
        key: `${architect}-${index}`,
        name: architect,
        regNo: "N/A",
        avatar: avatar || null,
      };
    }

    const name = architect?.name || `Architect ${index + 1}`;
    const regNo = architect?.regNo || "N/A";
    const avatar = architect?.logo || null;

    return {
      key: architect?.id || architect?.uuid || `${name}-${index}`,
      name,
      regNo,
      avatar,
    };
  });

  const showArchitectsSection = hasFetchedProjectPartners && architects.length > 0;

  const coverImageUrls = Array.isArray(project?.cover_images)
    ? project.cover_images
      .map((item) => {
        if (typeof item === "string") return resolveMediaUrl(item);
        if (item && typeof item === "object") {
          return resolveMediaUrl(
            item.image || item.cover || item.url || item.file,
          );
        }
        return null;
      })
      .filter(Boolean)
    : [];

  const fallbackImageUrl =
    resolveMediaUrl(project?.cover) || resolveMediaUrl(project?.image) || logo;
  const hasCoverGallery = coverImageUrls.length > 0;
  const showPlaceholder = !hasCoverGallery && fallbackImageUrl === logo;

  useEffect(() => {
    const fetchProjectData = async () => {
      setIsLoading(true);
      setError(null);
      setHasFetchedProjectPartners(false);
      setProjectPartners([]);
      try {
        // 1. Fetch Project Details
        const projectRes = await fetch(
          `${API_BASE_URL}api/all_projects/?limit=10&offset=0&search=${id}`,
        );
        const projectData = await projectRes.json();

        if (
          projectData.results &&
          projectData.results.features &&
          projectData.results.features.length > 0
        ) {
          const feature = projectData.results.features[0];
          const proj = {
            id: feature.id,
            ...feature.properties,
          };

          // Reverse Geocoding Logic
          if (feature.geometry && feature.geometry.coordinates) {
            const coords = feature.geometry.coordinates;
            // logic per user instructions: coords[0] is Lat, coords[1] is Lng
            if (coords.length >= 2) {
              const geocoded = await reverseGeocode(coords[0], coords[1]);
              proj.geo = geocoded;
            }
          }

          setProject(proj);

          // 2. Fetch Project Partners (architects) and normalize to: name, logo, type, url
          try {
            const authToken =
              localStorage.getItem("access_token") ||
              localStorage.getItem("token") ||
              localStorage.getItem("authToken");

            const partnerRes = await fetch(
              `${API_BASE_URL}${PROJECT_PARTNER_ENDPOINT}?project_id=${proj.id}`,
              {
                headers: authToken
                  ? {
                    Authorization: `Bearer ${authToken}`,
                  }
                  : {},
              },
            );

            if (partnerRes.ok) {
              const partnerData = await partnerRes.json();
              const partnerItems = Array.isArray(partnerData)
                ? partnerData
                : Array.isArray(partnerData?.results)
                  ? partnerData.results
                  : Array.isArray(partnerData?.data)
                    ? partnerData.data
                    : Array.isArray(partnerData?.partners)
                      ? partnerData.partners
                      : [];
              setProjectPartners(partnerItems.map(normalizePartnerItem));
            } else {
              console.error("Project partner API error:", partnerRes.status);
              setProjectPartners([]);
            }
          } catch (partnerErr) {
            console.error("Error fetching project partners:", partnerErr);
            setProjectPartners([]);
          } finally {
            setHasFetchedProjectPartners(true);
          }

          // 3. Fetch Announcements using internal UUID
          const annRes = await fetch(
            `${API_BASE_URL}api/anouncements/?id=${feature.id}&limit=100&offset=0&project_type=announcement`,
          );
          const annData = await annRes.json();
          setAnnouncements(
            Array.isArray(annData.results) ? annData.results : [],
          );

          // 4. Fetch Milestones using internal UUID
          const mileRes = await fetch(
            `${API_BASE_URL}api/anouncements/?id=${feature.id}&limit=100&offset=0&project_type=milestone`,
          );
          const mileData = await mileRes.json();
          setMilestones(
            Array.isArray(mileData.results) ? mileData.results : [],
          );
        } else {
          setError("Project not found");
        }
      } catch (err) {
        console.error("Error fetching project data:", err);
        setError("Failed to load project details");
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchProjectData();
    }
  }, [id]);

  useEffect(() => {
    if (!hasFetchedProjectPartners || projectPartners.length === 0) return;
    let cancelled = false;
    const fetchArchitectProfiles = async () => {
      const results = {};
      await Promise.all(
        projectPartners.map(async (partner) => {
          const regNo = partner?.regNo;
          if (!regNo || regNo === "N/A") return;
          try {
            const res = await fetch(
              `${process.env.REACT_APP_API_BASE}/auth/user/add_professional_to_project/?limit=10&offset=0&search=${encodeURIComponent(regNo)}`
            );
            if (!res.ok) return;
            const data = await res.json();
            const items = data?.results ?? (Array.isArray(data) ? data : []);
            results[regNo] = items.length > 0 ? items[0] : null;
          } catch {
            results[regNo] = null;
          }
        })
      );
      if (!cancelled) setArchitectProfiles(results);
    };
    fetchArchitectProfiles();
    return () => { cancelled = true; };
  }, [hasFetchedProjectPartners, projectPartners]);

  if (isLoading) {
    return (
      <div
        className="publish-container"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div className="status-message">Loading project details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="publish-container"
        style={{ textAlign: "center", paddingTop: "50px" }}
      >
        <h2 style={{ color: "#ef4444" }}>Error</h2>
        <p>{error}</p>
        <button
          onClick={handleBack}
          className="status-pill"
          style={{
            background: "#4f46e5",
            color: "white",
            border: "none",
            cursor: "pointer",
            marginTop: "20px",
          }}
        >
          Go Back
        </button>
      </div>
    );
  }

  if (!project) return null;

  return (
    <div className="publish-container" data-project-id={id}>
      {/* Header */}

      <header className="publish-header">
        <div className="publish-header-left">
          <MdArrowBack
            className="back-icon"
            onClick={handleBack}
            style={{ cursor: "pointer", fontSize: "21px" }}
          />
          <h1 className="publish-title">
            {project.name || "Untitled Project"}
          </h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            className="bookmark-button"
            title="Bookmark project"
            onClick={handleBookmark}
            style={{ background: 'none', border: 'none', cursor: 'pointer', marginRight: 0, padding: 0 }}
          >
            <FontAwesomeIcon icon={faBookmark} style={{ fontSize: 16, color: 'var(--color-primary)' }} />
          </button>
        </div>
      </header>


      {/* Featured Image Carousel */}
      <div className="publish-image-container">
        {hasCoverGallery ? (
          <div className="publish-image-strip" aria-label="Project cover images">
            {coverImageUrls.map((imageUrl, index) => (
              <img
                key={`${imageUrl}-${index}`}
                src={imageUrl}
                alt={`${project.name || "Project"} cover ${index + 1}`}
                className="publish-featured-image"
                onError={(e) => {
                  e.target.src = logo;
                  e.target.classList.add("placeholder-logo");
                }}
              />
            ))}
          </div>
        ) : (
          <img
            src={fallbackImageUrl}
            alt={project.name}
            className={`publish-featured-image ${showPlaceholder ? "placeholder-logo" : ""}`}
            onError={(e) => {
              e.target.src = logo;
              e.target.classList.add("placeholder-logo");
            }}
          />
        )}
      </div>

      {/* Project Code */}
      <div className="project-code">{project.code || id}</div>

      {/* Project Description */}
      <section>
        <h2 className="section-title">Project Description</h2>
        <p className="project-description">
          {project.description || "No description available for this project."}
        </p>
      </section>

      {/* Key Project Details */}
      <section>
        <h2 className="section-title">Key Project Details</h2>
        <div className="details-grid">
          <div className="detail-item">
            <span className="detail-label">Status</span>
            <div className="status-pill">
              {project.is_ongoing ? "In Progress" : "Completed"}
            </div>
          </div>
          <div className="detail-item">
            <span className="detail-label">Start date</span>
            <span className="detail-value">
              {formatDate(project.expected_start_date || project.start_date)}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Location</span>
            <span className="detail-value location-link" style={{ textDecoration: "none" }}>
              {(() => {
                const geo = project.geo;
                if (geo) {
                  const parts = [
                    geo.city || geo.county,
                    geo.country,
                  ].filter(Boolean);
                  if (parts.length > 0) return parts.join(", ");
                }
                return project.location || "Location unavailable";
              })()}
            </span>
          </div>
          {project.company_name && (
            <div className="detail-item">
              <span className="detail-label">Company</span>
              <div className="company-row">
                <div className="company-logo">
                  <div
                    style={{
                      width: "12px",
                      height: "12px",
                      background: "white",
                      borderRadius: "50%",
                      opacity: 0.8,
                    }}
                  ></div>
                </div>
                <a
                  href="https://www.nca.go.ke/registered-contractors"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="detail-value"
                  style={{ color: "var(--color-primary)", textDecoration: "none" }}
                >
                  {project.company_name}
                </a>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Announcements */}
      {showArchitectsSection && <section className="announcements-section">
        <div className="section-header-row">
          <h2 className="section-title" style={{ margin: 0 }}>
            Additional Information
          </h2>
        </div>

        <div className="architects-wrapper">
          <button
            type="button"
            className="architects-toggle"
            onClick={() => setIsArchitectsExpanded((prev) => !prev)}
            aria-expanded={isArchitectsExpanded}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              background: "none",
              border: "none",
              padding: "0",
              cursor: "pointer",
              marginBottom: isArchitectsExpanded ? "8px" : "0",
            }}
          >
            <span style={{ fontWeight: 600, fontSize: "15px", color: "#111" }}>
              Project Consultants
            </span>
            {isArchitectsExpanded ? <MdExpandLess style={{ color: "var(--color-primary)", fontSize: "22px" }} /> : <MdExpandMore style={{ color: "var(--color-primary)", fontSize: "22px" }} />}
          </button>

          {isArchitectsExpanded && (
            <>
              <p style={{ margin: "0 0 8px 0", fontSize: "13px", color: "#555" }}>
                Architect/Architectural Firm
              </p>
              <ul className="architects-list" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {architects.map((architect) => {
                  const hasProfile = architectProfiles[architect.regNo] != null;
                  return (
                    <li key={architect.key}>
                      <button
                        type="button"
                        className="architect-item"
                        style={{ display: "flex", alignItems: "center", gap: "12px", padding: "8px 0", textDecoration: "none", cursor: "pointer", background: "none", border: "none", width: "100%", textAlign: "left" }}
                        onClick={() => { setSelectedArchitect(architect); setArchitectProfileOpen(true); }}
                      >
                        {architect.avatar ? (
                          <img
                            src={architect.avatar}
                            alt={architect.name}
                            className="architect-avatar"
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        ) : (
                          <div className="architect-avatar architect-avatar-fallback">
                            {getArchitectInitials(architect.name)}
                          </div>
                        )}

                        <div className="architect-meta" style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                          <span className="architect-name" style={{ color: "#111", fontSize: "13px" }}>
                            Arch. {architect.name}
                          </span>
                          <span className="architect-reg" style={{ color: "var(--color-primary)", fontWeight: 500, fontSize: "13px" }}>
                            {architect.regNo}
                          </span>
                        </div>
                        {hasProfile && (
                          <a
                            href={`https://conra.net/solutions/borderless/portfolio/${encodeURIComponent(architect.regNo)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ marginLeft: "auto", flexShrink: 0, lineHeight: 0 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MdOpenInNew style={{ color: "var(--color-primary)", fontSize: "16px" }} />
                          </a>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>

      </section>}

      {/* Project Milestones */}
      <section className="milestones-section">
        <div className="section-header-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 className="section-title" style={{ margin: 0 }}>
            Project Milestones
          </h2>
          {/* <button
            onClick={() => setIsBuilding3DOpen(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: '5px',
              background: '#f0f4ff', border: '1.5px solid #a0b4f0',
              borderRadius: '8px', padding: '5px 10px',
              fontSize: '12px', fontWeight: 600, color: 'var(--color-primary)',
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            🏗️ 3D View
          </button> */}
        </div>

        <div className="milestones-container" style={{ display: 'flex', flexDirection: 'row', gap: '16px', width: '100%', overflowX: 'auto', paddingBottom: '8px', scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}>
          {milestones.length > 0 ? (
            milestones.map((ann, idx) => (
              <div
                key={ann.id || idx}
                onClick={() => navigate('/publish/milestone', { state: { milestone: ann, allMilestones: milestones, milestoneIndex: idx } })}
                style={{
                  cursor: 'pointer',
                  minWidth: 'min(240px, calc(87vw + 12px))',
                  width: 'min(240px, calc(87vw + 12px))',
                  flexShrink: 0,
                  background: '#f0f4ff',
                  border: '1.5px solid #a0b4f0',
                  borderRadius: '16px',
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                  scrollSnapAlign: 'start',
                }}
              >
                {/* Title row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <MdHistory style={{ color: 'var(--color-primary)', fontSize: '20px', flexShrink: 0 }} />
                  <span style={{ fontWeight: 700, fontSize: '15px', color: '#111' }}>
                    {ann.title || `Milestone ${idx + 1}`}
                  </span>
                </div>

                {/* Description */}
                <p style={{
                  margin: 0,
                  fontSize: '13px',
                  color: '#333',
                  lineHeight: '1.5',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}>
                  {ann.announcement || ann.text || ann.description || ''}
                </p>

                {/* Images — horizontal scroll */}
                {Array.isArray(ann.images) && ann.images.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                    {ann.images.slice(0, 2).map((img, i) => {
                      const src = img.image
                        ? (img.image.startsWith('http') ? img.image : `${MEDIA_PREFIX}${img.image}`)
                        : img;
                      return (
                        <img
                          key={i}
                          src={src}
                          alt="Milestone"
                          style={{ width: 'calc(50% - 4px)', height: '80px', objectFit: 'cover', borderRadius: '10px', flexShrink: 0 }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      );
                    })}
                  </div>
                )}

                {/* Date */}
                <p style={{ margin: 0, fontSize: '13px', color: '#444' }}>
                  Completed on {formatDate(ann.date || ann.created_at)}
                </p>
              </div>
            ))
          ) : (
            <p className="project-description">No milestones recorded.</p>
          )}
        </div>
      </section>
      {isBuilding3DOpen && (
        <Building3DView
          onClose={() => setIsBuilding3DOpen(false)}
          images={milestones.flatMap(m =>
            Array.isArray(m.images)
              ? m.images.map(img => img.image
                ? (img.image.startsWith('http') ? img.image : `${MEDIA_PREFIX}${img.image}`)
                : img)
              : []
          )}
        />
      )}
      <ArchitectProfileDialog
        isOpen={architectProfileOpen}
        onClose={() => setArchitectProfileOpen(false)}
        architect={selectedArchitect ? {
          name: selectedArchitect.name,
          avatar: selectedArchitect.avatar,
          regNo: selectedArchitect.regNo,
          title: "Registered Architect",
          location: project?.geo
            ? [project.geo.city || project.geo.county, project.geo.country].filter(Boolean).join(", ")
            : project?.location ?? "",
          stats: { total: 0, completed: 0, ongoing: 0 },
          featuredProjects: [],
        } : null}
        onEnquire={async ({ email, message }) => {
          try {
            const sendEmail = httpsCallable(getFunctions(app), 'sendEmail');
            await sendEmail({
              subject: `Project Enquiry: ${project?.name ?? 'Project'}`,
              html: `<p><strong>From:</strong> ${email}</p><p>${message}</p>`,
              replyTo: email,
            });
          } catch (err) {
            console.error('Failed to send enquiry:', err);
          }
          setArchitectProfileOpen(false);
        }}
      />
      <BookmarkModal
        isOpen={isBookmarkOpen}
        onClose={handleBookmarkClose}
        onSelectOption={handleBookmarkSelect}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
      />
      <EmailModal
        isOpen={isEmailModalOpen}
        onClose={handleEmailModalClose}
        onContinue={handleEmailContinue}
        email={email}
        setEmail={setEmail}
        rememberMe={rememberMe}
        setRememberMe={setRememberMe}
      />
      <WhatsAppModal
        isOpen={isWhatsAppModalOpen}
        onClose={handleWhatsAppModalClose}
        onContinue={handleWhatsAppContinue}
        whatsappNumber={whatsappNumber}
        setWhatsappNumber={setWhatsappNumber}
        rememberMe={rememberMe}
        setRememberMe={setRememberMe}
      />
      {bookmarkAlert && (
        <BookmarkAlert
          projectCode={bookmarkAlert.projectCode}
          method={bookmarkAlert.method}
          onClose={() => setBookmarkAlert(null)}
        />
      )}
    </div>
  );
};

export default ProjectPublish;
